import { type Key as SwrQueryKey } from 'swr';
import { GetUserActivityInstancesQueryDto } from '@/api/activityInstance';
import { GetLessonInstanceScoreByIdOptions } from '@/api/lessonInstance';
import { ContextualTranslateBySearchVocabsDto, ContextualTranslateByVocabIdsDto } from '@/api/translation.types';
import { SearchUsersDto } from '@/api/user';
import { GetUserWebappRoutesAccessParams } from '@/api/user-resource-access';
import { GetVocabQueryDto } from '@/api/vocab';
import { ActivityType } from '@/model/Activity';
import { AggregateFunctionName } from '@/model/common/AggregateFunctionName.enum';
import { LessonInstanceStatus } from '@/model/LessonInstance.model';
import { Language } from '@/model/Translation.model';
import { UserRole } from '@/model/User.model';

// * To avoid key collision, we centralize key getters here
// * Make sure there is no 2 getters return the same key

// remove any would requires typing of every single arguments
// oxlint-disable-next-line no-explicit-any
type KeyGetterFunction = (...args: any[]) => SwrQueryKey | null;

export const QueryKey = {
  // AccessCodes
  GetAccessCodeById: (id?: string) => (id ? `/access-codes/${id}` : null),
  GetAccessCodeGroups: () => '/edet/admin/access-code-groups',
  GetAccessCodeGroupById: (id?: string) => (id ? `/edet/admin/access-code-groups/${id}` : null),
  CreateAccessCodes: () => '/edet/admin/access-codes',

  // Users
  GetAuthenticatedUser: () => '/users/me',
  GetUserById: (id?: string) => (id ? `/users/${id}` : null),
  GetUsersByFilter: (query?: string) => (query ? `/users?${query}` : null),
  SearchUsers: (dto: SearchUsersDto) => (dto ? ['accessCode', 'firstName', 'lastName', dto] : null),
  GetUserAvatar: () => '/users/avatars',
  GetUserEdsyXp: (userId?: string) => (userId ? `/users/${userId}/xp` : null),
  GetUserEdsyCoin: (userId?: string) => (userId ? `/users/${userId}/coin` : null),

  // UserMetadata
  GetUserMetadataById: (id?: string) => (id ? `/user-metadata/${id}` : null),
  GetUserMetadataByUserId: (userId?: string) => (userId ? `/users/${userId}/metadata` : null),

  // Items
  GetItems: () => '/items',

  // Schools
  GetSchools: (query?: string) => (query ? `/schools?${query}` : null),
  GetSchoolById: (id?: string) => (id ? `/schools/${id}` : null),
  GetSchoolClassrooms: (id?: string) => (id ? `/schools/${id}/classrooms` : null),
  GetSchoolClassroomsWithStudentCount: (id?: string) => (id ? `/schools/${id}/classrooms-with-student-count` : null),
  GetSchoolUsers: (id: string | undefined, role?: UserRole) => (id ? `/schools/${id}/users?role=${role}` : null),
  GetSchoolClassroomSummary: (id?: string) => (id ? `/schools/${id}/classroom-summary` : null),
  GetSchoolSettingBySchoolId: (id?: string) => (id ? `/schools/${id}/settings` : null),

  // SchoolGroups
  GetUserInSchoolGroup: (query?: string) => (query ? `/school-groups/user?${query}` : null),
  GetSchoolGroupBySchoolId: (id?: string) => (id ? `/school-groups/school/${id}` : null),
  GetSchoolWithClassroomSummaryInSchoolGroup: () => `/school-groups/classroom-summary`,
  GetSchoolWithClassroomInSchoolGroup: () => `/school-groups/school-with-classroom`,
  GetOwnSchoolGroup: () => `/school-groups/me`,
  GetSchoolGroups: () => `/school-groups`,

  // Classrooms
  GetAllClassrooms: () => '/classrooms',
  GetClassroomsByUserId: (userId?: string) => (userId ? `/users/${userId}/classrooms` : null),
  GetClassroomById: (id?: string) => (id ? `/classrooms/${id}` : null),
  GetClassroomUsers: (id?: string) => (id ? `/classrooms/${id}/users` : null),
  GetClassroomAccessCodes: (id?: string) => (id ? `/classrooms/${id}/accesscodes` : null),
  GetClassroomActivationCodes: (id?: string) => (id ? `/classrooms/${id}/activation-codes` : null),
  GetClassroomTaxonomyTags: (classroomId?: string) => (classroomId ? `/classrooms/${classroomId}/taxonomy-tags` : null),

  // UserResourceAccess
  GetCurrentUserAccessibleClassrooms: () => '/user-resource-access/users/me/classrooms',
  GetUserAccessibleClassrooms: (id?: string) => (id ? `/user-resource-access/users/${id}/classrooms` : null),
  GetCurrentUserAccessibleSchools: () => '/user-resource-access/users/me/schools',
  GetCurrentUserAccessibleUsers: () => '/user-resource-access/users/users',
  GetUserWebappRoutesAccess: (userId: string | undefined, params: GetUserWebappRoutesAccessParams) => [
    '/user-resource-access/users/me/webapp-routes-access',
    `userId=${userId}`,
    `classroomId=${params.classroomId}`,
    `lessonInstanceId=${params.lessonInstanceId}`,
    `activityInstanceId=${params.activityInstanceId}`,
  ],

  // ClassroomLesson
  GetClassroomLessonsByClassroomId: (classroomId?: string) =>
    classroomId ? `/classrooms/${classroomId}/classroom-lessons` : null,
  GetClassroomLessonByClassroomIdAndLessonId: (classroomId?: string, lessonId?: string) =>
    classroomId && lessonId ? [`/classroom-lessons`, classroomId, lessonId] : null,

  // Lessons
  GetAllLessons: () => '/lessons',
  GetLessonById: (id?: string) => (id ? `/lessons/${id}` : null),
  GetLessonByIds: (ids?: string[]) => (ids ? `/lessons` : null),
  GetLessonsByClassroomId: (classroomId?: string) => (classroomId ? `/classrooms/${classroomId}/lessons` : null),
  GetLatestLessonInstancesByClassroomId: (classroomId?: string) =>
    classroomId ? `/classrooms/${classroomId}/latest-lesson-instances` : null,
  GetLessonInstancesByClassroomAndLesson: (classroomId?: string, lessonId?: string) =>
    classroomId ? `/classrooms/${classroomId}/lessons/${lessonId}/instances` : null,
  GetLessonInstanceById: (lessonInstanceId?: string) =>
    lessonInstanceId ? `/lesson-instances/${lessonInstanceId}` : null,
  GetTransactionsForReport: (lessonInstanceId?: string) =>
    lessonInstanceId ? `/lesson-instances/${lessonInstanceId}/report-transactions` : null,
  GetActivityReport: (lessonInstanceId?: string, activityInstanceId?: string) =>
    lessonInstanceId
      ? `/lesson-report/lesson-instance/${lessonInstanceId}/activity-instances/${activityInstanceId}/activity-report`
      : null,
  GetActivityReports: (lessonInstanceId?: string) =>
    lessonInstanceId ? `/lesson-report/lesson-instance/${lessonInstanceId}/activity-reports` : null,
  GetLessonInstancesByClassroomAndStatus: (classroomId?: string, status?: LessonInstanceStatus) =>
    classroomId ? `/classrooms/${classroomId}/lesson-instances?status=${status}` : null,
  GetLessonInstancesInClassroom: (classroomId?: string, status?: LessonInstanceStatus) =>
    classroomId ? `/lesson-instances/classroom/${classroomId}?status=${status}` : null,
  GetActivitiesWithAllTransactions: (lessonInstanceId?: string) =>
    lessonInstanceId ? `/lesson-instances/${lessonInstanceId}/activities-with-all-transaction` : null,
  getLessonInstancesByUserId: (userId?: string, status?: LessonInstanceStatus, includeDeleted?: boolean) =>
    userId ? `/lesson-instances?id=${userId}&status=${status}&includeDeleted=${includeDeleted}` : null,
  GetLessonInstanceScoreById: (
    lessonInstanceId: string | undefined,
    option: GetLessonInstanceScoreByIdOptions | undefined
  ) =>
    lessonInstanceId
      ? [`/lesson-instances/${lessonInstanceId}/score`, ...Object.entries(option ?? {}).map(([k, v]) => `${k}=${v}`)]
      : null,

  GetLessonsActivities: (lessonId?: string) => (lessonId ? `/lessons/${lessonId}/activities` : null),
  GetLessonScoreByLessonId: (lessonId: string | undefined, studentId?: string | undefined) =>
    lessonId ? [`/lessons/${lessonId}/score`, `studentId=${studentId}`] : null,
  GetOwnLessonInstanceStatusCount: (status: LessonInstanceStatus) => [`/lesson-instances/me/count?status=${status}`],

  // Activities
  GetActivityById: (activityId?: string) => (activityId ? `/activities/${activityId}` : null),
  GetActivityInLessonInstance: (lessonInstanceId?: string) =>
    lessonInstanceId ? `/lesson-instances/${lessonInstanceId}/activities` : null,
  GetRevampActivityCardData: (lessonInstanceId?: string) =>
    lessonInstanceId ? `/lesson-instances/${lessonInstanceId}/revamp-activity-card-data` : null,
  GetActivityMetadataByActivityId: (activityId?: string) => (activityId ? `/activities/${activityId}/metadata` : null),
  GetActivityScoreByActivityId: (activityId: string | undefined) =>
    activityId ? `/activities/${activityId}/score` : null,
  GetActivityScoresByActivityIds: (activityIds?: string[]) =>
    activityIds ? ['activities', 'scores', ...activityIds] : null,
  GetAggregatedActivityScoreById: (
    activityId: string | undefined,
    studentId: string | undefined,
    aggregateFunctionName: AggregateFunctionName | undefined
  ) =>
    activityId
      ? [`/activities/${activityId}/score`, 'studentId', studentId, 'aggregateFunction', aggregateFunctionName]
      : null,

  // ActivityInstances
  GetActivityInstance: (activityInstanceId?: string) =>
    activityInstanceId ? `/activity-instances/${activityInstanceId}` : null,
  GetNextTaskInActivityInstance: (activityInstanceId?: string) =>
    activityInstanceId ? `/activity-instances/${activityInstanceId}/next-task` : null,
  GetNextTaskAndTaskTransactionInActivityInstanceOEVocabLearning: (activityInstanceId?: string) =>
    activityInstanceId ? `/oe-vocab-learning-activity-instances/${activityInstanceId}/next-task` : null,
  GetAllTasksAndTaskTransactionsByActivityInstanceTypeAndId: (
    activityType: ActivityType | undefined,
    activityInstanceId: string | undefined
  ) =>
    activityType && activityInstanceId
      ? [
          // the BE url is `/${module-prefix}-activity-instances/${activityInstanceId}/all-tasks-and-task-transactions`
          activityType,
          'activity-instances',
          activityInstanceId,
          'all-tasks-and-task-transactions',
        ]
      : null,
  GetCurrentTaskAndTaskTransactionMutation: (activityInstanceId: string) =>
    `current-task-and-task-transaction-mutation/${activityInstanceId}`,
  GetActivityInstanceMetadataByActivityInstanceId: (activityInstanceId?: string) =>
    activityInstanceId ? `/activity-instances/${activityInstanceId}/metadata` : null,
  GetOpenAnswerGradingCriteriaConfig: (activityInstanceId?: string) =>
    activityInstanceId ? `/activity-instances/${activityInstanceId}/grading-criteria-config` : null,
  GetActivityInstanceProgressByActivityInstanceId: (activityInstanceId?: string) =>
    activityInstanceId ? `/activity-instances/${activityInstanceId}/progress` : null,
  GetActivityInstanceScoreByActivityInstanceId: (activityInstanceId: string | undefined) =>
    activityInstanceId ? `/activity-instances/${activityInstanceId}/score` : null,
  GetOwnActivityInstancesByQueryParams: (params: GetUserActivityInstancesQueryDto | undefined) =>
    params
      ? [
          `/activity-instances/me`,
          `lessonInstanceId=${params.lessonInstanceId}`,
          `activityId=${params.activityId}`,
          `classroomId=${params.classroomId}`,
          `status=${params.status}`,
          `type=${params.type}`,
        ]
      : null,
  GetStudentActivityInstancesByLessonInstanceIdAndActivityId: (
    studentId?: string,
    lessonInstanceId?: string,
    activityId?: string
  ) =>
    lessonInstanceId && activityId
      ? [
          `/activity-instances`,
          `userId=${studentId}`,
          `lessonInstanceId=${lessonInstanceId}`,
          `activityId=${activityId}`,
        ]
      : null,
  GetOwnActivityInstancesByLessonInstanceIdAndActivityId: (lessonInstanceId?: string, activityId?: string) =>
    lessonInstanceId && activityId
      ? [`/activity-instances/me`, `lessonInstanceId=${lessonInstanceId}`, `activityId=${activityId}`]
      : null,
  GetActivityInstanceTaskTransactions: (activityInstanceId?: string) =>
    activityInstanceId ? `/activity-instances/${activityInstanceId}/task-transactions` : null,
  GetActivityInstanceCompletePageData: (activityInstanceId?: string) =>
    activityInstanceId ? `/activity-instances/${activityInstanceId}/complete-page-data` : null,
  GetActivitySessionProgress: (activityInstanceId?: string) =>
    activityInstanceId ? `/activity-sessions/${activityInstanceId}/progress` : null,

  // Task
  GetTaskById: (taskId?: string) => (taskId ? `/tasks/${taskId}` : null),
  ReParaphrase: (taskId?: string) => (taskId ? `/tasks/${taskId}/re-paraphrase` : null),

  // TaskTransaction
  GetTaskTransactionById: (taskTransactionId?: string) =>
    taskTransactionId ? `/task-transactions/${taskTransactionId}` : null,

  // Leaderboard
  GetSummaryLeaderboard: (classroomId?: string) => (classroomId ? `/summary/leaderboard/${classroomId}` : null),
  GetSummaryScoreLeaderboard: (classroomId?: string) =>
    classroomId ? `/summary/score-leaderboard/${classroomId}` : null,
  getUserLeaderboard: (classroomId?: string) => (classroomId ? `/leaderboard/${classroomId}` : null),

  // EdsyPoint
  GetEdsyPointLeaderboard: (classroomId?: string) => (classroomId ? `edsy-point/leaderboard/${classroomId}` : null),
  GetEdsyPointUser: (userId?: string) => (userId ? `edsy-point/user-point/${userId}` : null),
  GetEdsyXpTransaction: () => `edsy-xp/me/transactions`,
  GetOwnEdsyXp: () => `edsy-xp/me`,
  GetOwnEdsyXpAndCoinTransactions: () => `edsy-xp-and-coin/me/transactions`,
  GetEdsyCoinTransaction: () => `edsy-coin/me/transactions`,
  GetOwnEdsyCoin: () => `edsy-coin/me`,
  GetUserPointHistories: (userId?: string) => (userId ? `edsy-point/user-point/history/${userId}` : null),

  GetUserSummary: (userId?: string) => (userId ? `/summary/users/${userId}` : null),

  // Classroom Summary
  GetLessonInstancesByStudentId: (classroomId?: string, studentId?: string) =>
    classroomId && studentId ? `/classroom-summary/${classroomId}/students/${studentId}/lesson-instances` : null,
  GetRecentLessonInstancesByStudent: (classroomId?: string, studentId?: string) =>
    classroomId && studentId ? `/classroom-summary/${classroomId}/students/${studentId}/recent-instances` : null,
  GetLessonInstancesByClassroomId: (classroomId?: string) =>
    classroomId ? `/classroom-summary/${classroomId}/lesson-instances` : null,
  GetLessonSummariesByClassroomId: (classroomId?: string) =>
    classroomId ? `/classroom-summary/${classroomId}/lesson-summaries` : null,
  GetLessonSummariesWithScoreByClassroomId: (classroomId?: string) =>
    classroomId ? `/classroom-summary/${classroomId}/lesson-summaries-with-score` : null,
  GetActivitySummariesWithScoreByClassroomId: (classroomId?: string) =>
    classroomId ? `/classroom-summary/${classroomId}/activity-summaries-with-score` : null,
  GetMispronunciationsByClassroomId: (classroomId?: string) =>
    classroomId ? `/classroom-summary/${classroomId}/mispronunciations` : null,
  GetLessonSummaryByClassroom: (classroomId?: string) =>
    classroomId ? `/classroom-summary/${classroomId}/lessons-summary` : null,
  GetLessonInstancesByLesson: (classroomId?: string, lessonId?: string) =>
    classroomId && lessonId ? `/classroom-summary/${classroomId}/lessons/${lessonId}/lesson-instances` : null,
  GetLessonStudentProgressSummary: (classroomId?: string, lessonId?: string) =>
    classroomId && lessonId ? [`/classrooms/${classroomId}/lessons/${lessonId}/students-progress-summary`] : null,
  GetLessonAverageStudentsScore: (classroomId?: string, lessonId?: string) =>
    classroomId && lessonId ? [`/classrooms/${classroomId}/lessons/${lessonId}/average-students-score`] : null,
  GetLessonAverageScoreByActivity: (classroomId?: string, lessonId?: string) =>
    classroomId && lessonId ? [`/classrooms/${classroomId}/lessons/${lessonId}/average-score-by-activity`] : null,
  GetSchoolLessonStatusTableData: (classroomId?: string, lessonId?: string, status?: `${LessonInstanceStatus}`) =>
    classroomId && lessonId
      ? [`/classroom-summary/${classroomId}/lessons/${lessonId}/status-table-data`, { status }]
      : null,

  // Speech
  GetSpeechSynthesis: (text?: string, speed?: number, voiceModel?: string) =>
    `/speech/synthesis?speed=${speed ?? 1}&text=${text ?? ''}&voiceModel=${voiceModel ?? ''}`,

  // Translation
  GetTranslations: (texts: string[], targetLanguage: Language) =>
    texts.length > 0 ? ['translation', targetLanguage, ...texts] : null,
  GetContextualTranslateByVocabIds: (dto?: ContextualTranslateByVocabIdsDto) =>
    dto ? ['translation', 'contextual', 'vocabIds', dto] : null,
  GetContextualTranslateBySearchVocabs: (dto?: ContextualTranslateBySearchVocabsDto) =>
    dto ? ['translation', 'contextual', 'searchVocabs', dto] : null,
  // Vocabs
  GetVocabById: (vocabId: string, query: GetVocabQueryDto = {}) =>
    vocabId ? `/vocabs/${vocabId}?${new URLSearchParams(query)}` : null,
  GetVocabsByIds: (vocabIds: string[], queries: GetVocabQueryDto[]) =>
    vocabIds?.length > 0 ? [`/vocabs/bulk-get`, ...vocabIds, ...queries] : null,
  GetVocab: (text?: string) => (text ? ['/vocabs/search', text] : null),

  // UserVocabPracticeAttempt
  GetUserVocabPracticeAttempts: () => `/user-vocab-practice-attempts`,

  // Data Exports
  GetActivityResultCsvByClassroom: (classroomId: string) =>
    `/data-exports/student-activity-data-by-classroom/${classroomId}`,
  GetActivityResultCsvByClassroomAndLesson: (classroomId: string) =>
    `/data-exports/student-activity-data-by-classroom-and-lesson/${classroomId}/lessons/:lessonId`,

  // ClassroomLessonState
  GetClassroomLessonState: (classroomId?: string) =>
    classroomId ? `/classroom-lesson-state/classrooms/${classroomId}` : null,
  GetLessonSelectionPageData: (classroomId?: string) =>
    classroomId ? `/classroom-lesson-state/classrooms/${classroomId}/lesson-selection-page-data` : null,
  GetRevampLessonCardData: (classroomId?: string) =>
    classroomId ? `/classroom-lesson-state/classrooms/${classroomId}/revamp-lesson-card-data` : null,
  GetLessonStateByClassroomIdAndLessonId: (classroomId?: string, lessonId?: string) =>
    classroomId && lessonId ? `/classroom-lesson-state/classrooms/${classroomId}/lessons/${lessonId}` : null,

  // ClassroomState
  GetUserAllClassroomStates: () => `/classroom-state/me`,

  //ClassroomActivationCode
  GetCurrentUsageActivationCodeByClassroomId: (id?: string) =>
    id ? `/classroom-activation-codes/current-usage-activation-code?classroomId=${id}` : null,
  GetClassroomActivationCodeValidationByCode: (code?: string) =>
    code ? `/classroom-activation-codes/validate/${code}` : null,

  // ClassroomReport
  GetClassroomReportPrintingData: (classroomId?: string) =>
    classroomId ? `/classroom-report/classrooms/${classroomId}/printing-data` : null,
  GetClassroomReportScore: (classroomId?: string) =>
    classroomId ? `/classroom-report/classrooms/${classroomId}/score` : null,
  GetClassroomReportEosData: (cacheKey?: string) =>
    cacheKey ? `/classroom-report/eos/data?cacheKey=${cacheKey}` : null,
  GetClassroomReportAllStudentScore: (classroomId?: string) =>
    classroomId ? `/classroom-report/classrooms/${classroomId}/users/score` : null,
  // AI
  GetSentenceSimilarity: (firstSentence: string, secondSentence: string) => [
    '/ai/sentence-similarity',
    firstSentence,
    secondSentence,
  ],

  // ActivityBank
  GetSimpleQuizLessonTemplates: (ignored_params?: unknown) => [`/activity-banks/simple-quiz-templates`, ignored_params],
  GetActivityBankOpsSheetData: (sheetId: string | null | undefined, sheetName: string | null | undefined) =>
    sheetId && sheetName
      ? [`/activity-banks/google-sheet-ops-template/sheetId/${sheetId}/sheetName/${sheetName}`]
      : null,
  GetActivityBankLessonTemplateSheetData: (sheetId: string | null | undefined, sheetName: string | null | undefined) =>
    sheetId && sheetName
      ? [`/activity-banks/google-sheet-lesson-template/sheetId/${sheetId}/sheetName/${sheetName}`]
      : null,

  // TaskGradingCriteria
  GetPronunciationCriteria: () => ['/task-grading-criteria/pronunciation'],

  // ActivityGradingCriteria
  GetActivityGradingCriteria: () => '/activity-grading-criteria',

  // LessonGradingCriteria
  GetLessonGradingCriteria: () => '/lesson-grading-criteria',

  // CheatDetection
  GetCheatDetection: (query?: string) => `/cheat-detection?${query}`,

  // AuditLogs
  GetAuditLogByResourceId: (resource?: string, id?: string, action?: string) =>
    `audit-logs/by-resource-id/most-recent?resource=${resource}&resourceId=${id}&action=${action}`,
  GetUserActivitiesAuditLogs: (userId?: string) => (userId ? `audit-logs/users/${userId}/activities` : null),

  // Response Audio
  GetResponseAudioBlobFromPath: (responseFilePath: string | null | undefined) =>
    responseFilePath ? `gcp-storage/audio?path=${responseFilePath}` : null,

  // Portfolio
  GetPortfolioProgress: () => 'portfolio/progress',
  GetPortfolioAudioBlobFromPath: (audioPath: string | null | undefined) =>
    audioPath ? `portfolio/audio?path=${audioPath}` : null,
} satisfies Record<string, KeyGetterFunction>;
